import { Form, Card, Badge } from 'react-bootstrap'

interface PromptConfigSectionProps {
  isOrgScope: boolean
  promptHeader: string
  onPromptHeaderChange: (value: string) => void
  promptBody: string
  onPromptBodyChange: (value: string) => void
  promptFooter: string
  onPromptFooterChange: (value: string) => void
  shareContactInfo: boolean | null
  onShareContactInfoChange: (value: boolean | null) => void
  shareQuotes: boolean | null
  onShareQuotesChange: (value: boolean | null) => void
  sharePolicies: boolean | null
  onSharePoliciesChange: (value: boolean | null) => void
  shareLeads: boolean | null
  onShareLeadsChange: (value: boolean | null) => void
  shareConsentStatus: boolean | null
  onShareConsentStatusChange: (value: boolean | null) => void
}

export const PromptConfigSection = ({
  isOrgScope,
  promptHeader,
  onPromptHeaderChange,
  promptBody,
  onPromptBodyChange,
  promptFooter,
  onPromptFooterChange,
  shareContactInfo,
  onShareContactInfoChange,
  shareQuotes,
  onShareQuotesChange,
  sharePolicies,
  onSharePoliciesChange,
  shareLeads,
  onShareLeadsChange,
  shareConsentStatus,
  onShareConsentStatusChange,
}: PromptConfigSectionProps) => {
  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Header className="bg-light border-0 py-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <span className="fs-3">📝</span>
            <h5 className="fw-bold text-gray-800 mb-0">Conversational AI System Prompt</h5>
          </div>
          <Badge bg="secondary">{isOrgScope ? 'Organization' : 'Customization'}</Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Label>
            Header (initial persona & system role)
            {!isOrgScope && <span className="text-muted"> — editable at organization level only</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={promptHeader}
            onChange={(e) => onPromptHeaderChange(e.target.value)}
            disabled={!isOrgScope}
            placeholder="e.g.: You are an insurance advisor. Respond in a friendly, professional, and concise manner."
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Body (level-specific instructions)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={promptBody}
            onChange={(e) => onPromptBodyChange(e.target.value)}
            placeholder="Additional instructions for this level. Appended to the rest of the inheritance chain."
          />
          <Form.Text className="text-muted d-block mt-2">
            The final prompt combines organization, group, and agent body texts — this text is appended and does not overwrite organization defaults.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Footer (constraints & safety limitations)
            {!isOrgScope && <span className="text-muted"> — editable at organization level only</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={promptFooter}
            onChange={(e) => onPromptFooterChange(e.target.value)}
            disabled={!isOrgScope}
            placeholder="e.g.: Do not invent information that is not available."
          />
        </Form.Group>

        <hr />

        <Form.Label className="mb-2">Customer context shared with the LLM</Form.Label>
        <Form.Text className="text-muted d-block mb-2">
          Only referential context is shared (never sensitive data like SSN, financial amounts, or document files).
        </Form.Text>

        <Form.Check
          type="checkbox"
          id="share-contact-info"
          label="Contact (name & status) — default"
          checked={!!shareContactInfo}
          onChange={(e) => onShareContactInfoChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-quotes"
          label="Quotes (status, summary)"
          checked={!!shareQuotes}
          onChange={(e) => onShareQuotesChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-policies"
          label="Policies (type & status, without premiums or IDs)"
          checked={!!sharePolicies}
          onChange={(e) => onSharePoliciesChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-leads"
          label="Leads (stage & intent)"
          checked={!!shareLeads}
          onChange={(e) => onShareLeadsChange(e.target.checked)}
          className="mb-2"
        />
        <Form.Check
          type="checkbox"
          id="share-consent-status"
          label="Consent status (signed/pending, without document files)"
          checked={!!shareConsentStatus}
          onChange={(e) => onShareConsentStatusChange(e.target.checked)}
          className="mb-2"
        />
      </Card.Body>
    </Card>
  )
}
